import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Mic, MicOff } from "lucide-react";

/** Web Speech API — browser-native, no cost. Returns interim + final transcripts.
 *  On stop/onend, any pending interim string is flushed as final so the user's
 *  last uncommitted words are never silently dropped. */
export function useSpeechRecognition() {
  const [isListening, setIsListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState("");
  const recognitionRef = useRef(null);
  const onFinalRef = useRef(null);
  const interimRef = useRef("");   // tracks last uncommitted interim for stop-flush

  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const start = useCallback((onFinal) => {
    setError("");
    setInterim("");
    interimRef.current = "";
    onFinalRef.current = onFinal;
    if (!supported) { setError("Voice input is not supported in this browser. Try Chrome or Edge."); return; }
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.onresult = (ev) => {
      let interimStr = "";
      let finalStr = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalStr += t + " ";
        else interimStr += t;
      }
      setInterim(interimStr);
      interimRef.current = interimStr;
      if (finalStr && onFinalRef.current) {
        onFinalRef.current(finalStr.trim());
        // finalization consumed the previously pending interim
        interimRef.current = "";
      }
    };
    rec.onerror = (ev) => setError(ev.error || "Speech error");
    rec.onend = () => {
      // Flush any pending interim as if it were final so words aren't lost
      // when the user hits Stop before Chrome finalizes the last utterance.
      const pending = (interimRef.current || "").trim();
      if (pending && onFinalRef.current) {
        try { onFinalRef.current(pending); } catch {}
      }
      interimRef.current = "";
      setInterim("");
      setIsListening(false);
    };
    recognitionRef.current = rec;
    rec.start();
    setIsListening(true);
  }, [supported]);

  const stop = useCallback(() => {
    // onend handles flush + state reset; do NOT clear interim here or the
    // flush loses the string.
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
  }, []);

  return { supported: !!supported, isListening, interim, error, start, stop };
}

/** Textarea with integrated live voice dictation.
 *  While listening, the textarea temporarily displays committed + interim,
 *  so the member's words appear visually inside the field they are filling.
 *  The persisted source of truth stays `value` (parent-controlled).
 *
 *  Props:
 *    value        current persisted text
 *    onChange(v)  called with the new committed text (after Chrome finalizes
 *                 a chunk, or when the user clicks Stop and pending interim
 *                 is flushed)
 *    rows, placeholder, className   forwarded to <textarea>
 *    testid       used as the mic button test id and prefix for child ids
 *    ariaLabel    accessible label for the textarea */
export function VoiceTextarea({ value, onChange, rows = 6, placeholder, className, testid = "voice-textarea", ariaLabel }) {
  const { supported, isListening, interim, error, start, stop } = useSpeechRecognition();
  const committedRef = useRef(value || "");
  useEffect(() => { committedRef.current = value || ""; }, [value]);

  const handleFinal = (finalText) => {
    if (!finalText) return;
    const base = committedRef.current || "";
    const next = base ? base + " " + finalText : finalText;
    committedRef.current = next;
    onChange(next);
  };

  const displayValue = isListening
    ? ((committedRef.current || "") + (interim ? ((committedRef.current ? " " : "") + interim) : ""))
    : (value || "");

  return (
    <div className={`relative ${isListening ? "rounded-md ring-2 ring-red-400" : ""}`}>
      <textarea
        data-testid={`${testid}-textarea`}
        rows={rows}
        className={className}
        value={displayValue}
        onChange={(e) => { if (!isListening) onChange(e.target.value); }}
        readOnly={isListening}
        aria-label={ariaLabel}
        placeholder={placeholder}
      />
      {supported && (
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {!isListening ? (
            <Button type="button" size="sm" variant="outline" onClick={() => start(handleFinal)} data-testid={testid}>
              <Mic className="h-4 w-4 mr-1"/>Speak Instead
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={stop} className="bg-red-600 hover:bg-red-700 text-white animate-pulse" data-testid={`${testid}-stop`}>
              <MicOff className="h-4 w-4 mr-1"/>Stop
            </Button>
          )}
          {isListening && (
            <span className="text-xs text-red-600 font-medium" data-testid={`${testid}-live-indicator`}>
              Listening — your words appear in the field above. Click Stop when done.
            </span>
          )}
          {error && <span className="text-xs text-amber-700">{error}</span>}
        </div>
      )}
    </div>
  );
}

/** MediaRecorder — records audio blob. */
export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);

  const start = useCallback(async () => {
    setError(""); setBlob(null); chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setBlob(b);
        stream.getTracks().forEach(t => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
      };
      mediaRef.current = mr;
      startedAtRef.current = Date.now();
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(Math.floor((Date.now() - startedAtRef.current)/1000)), 500);
      mr.start();
      setIsRecording(true);
    } catch (e) {
      setError(e.message || "Microphone permission denied");
    }
  }, []);

  const stop = useCallback(() => {
    if (mediaRef.current && isRecording) mediaRef.current.stop();
    setIsRecording(false);
  }, [isRecording]);

  const reset = useCallback(() => { setBlob(null); setDuration(0); chunksRef.current = []; }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  return { isRecording, blob, duration, error, start, stop, reset };
}
